import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function UserDashboard({ currentUser }) {
  const [dashboardData, setDashboardData] = useState({
    fraudCases: [],
    grantApplications: [],
    providerRegistrations: [],
    thesisSubmissions: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('fraud');

  useEffect(() => {
    if (currentUser && currentUser.id) {
      fetchDashboardData();
    }
  }, [currentUser]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/user/dashboard?userId=${currentUser.id}`);
      const result = await response.json();

      if (result.success) {
        setDashboardData(result.data);
      } else {
        setError(result.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      setError('An error occurred while loading your dashboard');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Pending': { bg: '#fff3cd', color: '#856404', border: '#ffc107' },
      'Under Investigation': { bg: '#cfe2ff', color: '#084298', border: '#0d6efd' },
      'Resolved': { bg: '#d1e7dd', color: '#0f5132', border: '#198754' },
      'Closed': { bg: '#e2e3e5', color: '#41464b', border: '#6c757d' },
      'Approved': { bg: '#d1e7dd', color: '#0f5132', border: '#198754' },
      'Rejected': { bg: '#f8d7da', color: '#842029', border: '#dc3545' },
      'Pending Review': { bg: '#fff3cd', color: '#856404', border: '#ffc107' },
      'Active': { bg: '#d1e7dd', color: '#0f5132', border: '#198754' },
      'IN REVIEW': { bg: '#cfe2ff', color: '#084298', border: '#0d6efd' },
      'VERIFIED': { bg: '#d1e7dd', color: '#0f5132', border: '#198754' },
      'CORRECTIONS NEEDED': { bg: '#fff3cd', color: '#856404', border: '#ffc107' }
    };

    const style = statusColors[status] || { bg: '#e2e3e5', color: '#41464b', border: '#6c757d' };

    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        border: `2px solid ${style.border}`,
        padding: '6px 12px',
        borderRadius: '5px',
        fontSize: '13px',
        fontWeight: 'bold',
        display: 'inline-block'
      }}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <h2>My Dashboard</h2>
        <div style={{ textAlign: 'center', padding: '50px', fontSize: '18px', color: '#666' }}>
          Loading your dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <h2>My Dashboard</h2>
        <div style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '20px',
          borderRadius: '8px',
          marginTop: '20px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  const totalSubmissions =
    dashboardData.fraudCases.length +
    dashboardData.providerRegistrations.length;

  return (
    <div className="tab-content">
      <h2>My Dashboard</h2>

      {/* User Info Card */}
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '2px solid #dee2e6',
        borderRadius: '10px',
        padding: '25px',
        marginBottom: '30px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 5px 0' }}>Name</p>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#2c3e50', margin: 0 }}>
              {currentUser.name}
            </p>
          </div>
          <div>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 5px 0' }}>Email</p>
            <p style={{ fontSize: '16px', color: '#2c3e50', margin: 0 }}>
              {currentUser.email}
            </p>
          </div>
          {currentUser.occupation && (
            <div>
              <p style={{ fontSize: '14px', color: '#666', margin: '0 0 5px 0' }}>Occupation</p>
              <p style={{ fontSize: '16px', color: '#2c3e50', margin: 0 }}>
                {currentUser.occupation}
              </p>
            </div>
          )}
          <div>
            <p style={{ fontSize: '14px', color: '#666', margin: '0 0 5px 0' }}>Total Submissions</p>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#3498db', margin: 0 }}>
              {totalSubmissions}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '25px',
        borderBottom: '2px solid #ddd',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setActiveSection('fraud')}
          style={{
            backgroundColor: activeSection === 'fraud' ? '#e74c3c' : 'transparent',
            color: activeSection === 'fraud' ? 'white' : '#e74c3c',
            border: 'none',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            borderBottom: activeSection === 'fraud' ? '3px solid #e74c3c' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          Fraud Reports ({dashboardData.fraudCases.length})
        </button>
        {/* Grant Applications tab disabled */}
        <button
          onClick={() => setActiveSection('providers')}
          style={{
            backgroundColor: activeSection === 'providers' ? '#3498db' : 'transparent',
            color: activeSection === 'providers' ? 'white' : '#3498db',
            border: 'none',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            borderBottom: activeSection === 'providers' ? '3px solid #3498db' : 'none',
            transition: 'all 0.3s ease'
          }}
        >
          Provider Registrations ({dashboardData.providerRegistrations.length})
        </button>
        {/* Thesis Submissions tab hidden */}
      </div>

      {/* Fraud Reports Section */}
      {activeSection === 'fraud' && (
        <div>
          <h3 style={{ color: '#e74c3c', marginBottom: '20px' }}>My Fraud Reports</h3>
          {dashboardData.fraudCases.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '10px' }}>No fraud reports submitted yet</p>
              <p style={{ fontSize: '14px' }}>When you file a fraud complaint, it will appear here</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {dashboardData.fraudCases.map((fraudCase, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #e74c3c',
                    borderRadius: '8px',
                    padding: '20px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <h4 style={{ color: '#2c3e50', margin: '0 0 5px 0' }}>{fraudCase.name}</h4>
                      <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                        Case ID: {fraudCase.RowKey}
                      </p>
                    </div>
                    {getStatusBadge(fraudCase.status || 'Pending')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Reported</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>{fraudCase.reported}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Violation Type</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>{fraudCase.violation}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Severity</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>{fraudCase.severity}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Submitted</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {formatDate(fraudCase.Timestamp)}
                      </p>
                    </div>
                  </div>
                  {fraudCase.description && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 5px 0' }}>Description</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0, lineHeight: '1.6' }}>
                        {fraudCase.description}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Grant Applications Section disabled */}

      {/* Provider Registrations Section */}
      {activeSection === 'providers' && (
        <div>
          <h3 style={{ color: '#3498db', marginBottom: '20px' }}>My Provider Registrations</h3>
          {dashboardData.providerRegistrations.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}>
              <p style={{ fontSize: '18px', marginBottom: '10px' }}>No provider registrations submitted yet</p>
              <p style={{ fontSize: '14px' }}>When you register as a provider, it will appear here</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {dashboardData.providerRegistrations.map((provider, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #3498db',
                    borderRadius: '8px',
                    padding: '20px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <h4 style={{ color: '#2c3e50', margin: '0 0 5px 0' }}>{provider.name}</h4>
                      <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                        Registration ID: {provider.RowKey}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(provider.approvalStatus || provider.status || 'Pending Review')}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Specialization</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>{provider.specialization}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Location</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>{provider.location}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Payment Status</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {provider.paymentStatus || 'Pending'}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Submitted</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {formatDate(provider.submittedDate)}
                      </p>
                    </div>
                  </div>
                  {provider.website && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 5px 0' }}>Website</p>
                      <a
                        href={provider.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: '14px', color: '#3498db', textDecoration: 'none' }}
                      >
                        {provider.website}
                      </a>
                    </div>
                  )}
                  {provider.barcodeId && (
                    <div style={{
                      marginTop: '15px',
                      paddingTop: '15px',
                      borderTop: '1px solid #eee',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '15px'
                    }}>
                      <div>
                        <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Provider Barcode</p>
                        <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#27ae60', margin: 0 }}>
                          {provider.barcodeId}
                        </p>
                      </div>
                      {provider.verifiedSince && (
                        <div>
                          <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Verified Since</p>
                          <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                            {provider.verifiedSince}
                          </p>
                        </div>
                      )}
                      {provider.expiryDate && (
                        <div>
                          <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Subscription Expires</p>
                          <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                            {formatDate(provider.expiryDate)}
                          </p>
                        </div>
                      )}
                      {provider.daysRemaining !== null && provider.daysRemaining !== undefined && (
                        <div>
                          <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Days Remaining</p>
                          <p style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            margin: 0,
                            color: provider.daysRemaining < 0
                              ? '#c62828'
                              : provider.daysRemaining < 30
                                ? '#c62828'
                                : provider.daysRemaining < 60
                                  ? '#f39c12'
                                  : '#27ae60'
                          }}>
                            {provider.daysRemaining < 0
                              ? `Expired ${Math.abs(provider.daysRemaining)} day(s) ago`
                              : `${provider.daysRemaining} day(s)`}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Thesis Submissions Section */}
      {activeSection === 'thesis' && (
        <div>
          <h3 style={{ color: '#8e44ad', marginBottom: '20px' }}>My Thesis Submissions</h3>
          {dashboardData.thesisSubmissions.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #dee2e6'
            }}>
              <p style={{ color: '#6c757d', fontSize: '16px', margin: 0 }}>
                No thesis submissions yet. Submit your thesis for verification!
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {dashboardData.thesisSubmissions.map((thesis, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '20px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '15px'
                  }}>
                    <div>
                      <h4 style={{ color: '#2c3e50', margin: '0 0 8px 0', fontSize: '18px' }}>
                        {thesis.documentTitle || 'Untitled'}
                      </h4>
                      <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '0 0 5px 0' }}>
                        Submission ID: <strong>{thesis.RowKey}</strong>
                      </p>
                    </div>
                    {getStatusBadge(thesis.status || 'IN REVIEW')}
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '15px',
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '1px solid #eee'
                  }}>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Document Type</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {thesis.documentType || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Course</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {thesis.course || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>University</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {thesis.universityName || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 3px 0' }}>Submitted On</p>
                      <p style={{ fontSize: '14px', color: '#2c3e50', margin: 0 }}>
                        {formatDate(thesis.submissionDate)}
                      </p>
                    </div>
                  </div>

                  {thesis.barcodeId && (
                    <div style={{
                      marginTop: '15px',
                      paddingTop: '15px',
                      borderTop: '1px solid #eee',
                      backgroundColor: '#f8f9fa',
                      padding: '12px',
                      borderRadius: '6px'
                    }}>
                      <p style={{ fontSize: '12px', color: '#666', margin: '0 0 5px 0' }}>Authenticity Barcode</p>
                      <p style={{ fontSize: '16px', color: '#27ae60', fontWeight: 'bold', margin: 0 }}>
                        {thesis.barcodeId}
                      </p>
                      {thesis.authenticityScore && (
                        <p style={{ fontSize: '14px', color: '#2c3e50', margin: '5px 0 0 0' }}>
                          Score: <strong>{thesis.authenticityScore}</strong>
                        </p>
                      )}
                    </div>
                  )}

                  {/* QR Code and Download Options for VERIFIED theses */}
                  {thesis.status === 'VERIFIED' && thesis.barcodeId && (
                    <div style={{ marginTop: '15px' }}>
                      <div style={{
                        padding: '15px',
                        backgroundColor: '#e8f8f5',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        textAlign: 'center'
                      }}>
                        <strong style={{ color: '#27ae60', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                          Verification QR Code
                        </strong>
                        <QRCodeCanvas
                          value={`${window.location.origin}/verify/thesis/${thesis.barcodeId}`}
                          size={150}
                          level="H"
                          style={{ margin: '0 auto' }}
                        />
                        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#7f8c8d' }}>
                          Scan to verify thesis authenticity
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          window.open(`${API_URL}/thesis-submissions/${thesis.RowKey}/download-certificate`, '_blank');
                        }}
                        style={{
                          backgroundColor: '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          width: '100%',
                          marginBottom: '10px'
                        }}
                      >
                        Download Certificate (PDF)
                      </button>
                      <button
                        onClick={() => {
                          window.open(`${API_URL}/thesis-submissions/${thesis.RowKey}/download-report`, '_blank');
                        }}
                        style={{
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          width: '100%'
                        }}
                      >
                        Download Detailed Report (PDF)
                      </button>
                    </div>
                  )}

                  {/* Download Report for REJECTED theses */}
                  {thesis.status === 'REJECTED' && (
                    <div style={{ marginTop: '15px' }}>
                      <button
                        onClick={() => {
                          window.open(`${API_URL}/thesis-submissions/${thesis.RowKey}/download-report`, '_blank');
                        }}
                        style={{
                          backgroundColor: '#3498db',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          width: '100%'
                        }}
                      >
                        Download Detailed Report (PDF)
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserDashboard;
